import React from 'react';
import './IntervalChart.css';

interface IntervalChartProps {
  intervals: any[];
  height?: number;
  showTooltips?: boolean;
  showLabels?: boolean;
}

export const ZONE_COLORS: Record<string, string> = {
  zone1: '#b0b0b0', zone2: '#4a90d9', zone3: '#5cb85c',
  zone4: '#f0ad4e', zone5: '#d9534f', zone6: '#1a1a1a',
};
const ZONE_HEIGHTS: Record<string, number> = {
  zone1: 25, zone2: 45, zone3: 60, zone4: 80, zone5: 90, zone6: 100,
};

const ZONE_NAME_MAP: Record<string, string> = {
  recovery: 'zone1', endurance: 'zone2', tempo: 'zone3',
  threshold: 'zone4', lactate_threshold: 'zone4',
  vo2max: 'zone5', vo2_max: 'zone5',
  anaerobic: 'zone6', neuromuscular: 'zone6',
};

function normalizeZone(zone: string | undefined): string {
  if (!zone) return 'zone2';
  if (zone.startsWith('zone')) return zone;
  return ZONE_NAME_MAP[zone.toLowerCase()] || 'zone2';
}

function getZone(seg: any): string {
  return normalizeZone(seg.zone || seg.on_zone || seg.onZone);
}

function getColor(zone: string): string {
  return ZONE_COLORS[zone] || ZONE_COLORS.zone2;
}

function getHeight(zone: string): number {
  return ZONE_HEIGHTS[zone] || 45;
}

interface BarData {
  zone: string;
  duration: number;
  label: string;
  watts?: number;
}

function expandIntervals(intervals: any[]): BarData[] {
  const bars: BarData[] = [];
  for (const seg of intervals) {
    const type = seg.type || 'steady';
    if (type === 'warmup' || type === 'cooldown') {
      const dur = seg.duration || 300;
      const targetZone = normalizeZone(seg.zone);
      if (type === 'warmup') {
        const third = Math.round(dur / 3);
        bars.push({ zone: 'zone1', duration: third, label: 'Warmup Z1' });
        bars.push({ zone: 'zone1', duration: third, label: 'Warmup Z1→' + targetZone });
        bars.push({ zone: targetZone, duration: dur - 2 * third, label: `Warmup ${targetZone.toUpperCase()}` });
      } else {
        const third = Math.round(dur / 3);
        bars.push({ zone: targetZone, duration: third, label: `Cooldown ${targetZone.toUpperCase()}` });
        bars.push({ zone: 'zone1', duration: third, label: 'Cooldown Z1' });
        bars.push({ zone: 'zone1', duration: dur - 2 * third, label: 'Cooldown Z1' });
      }
    } else if (type === 'steady') {
      const zone = getZone(seg);
      bars.push({ zone, duration: seg.duration || 600, label: `${zone.replace('zone', 'Z')}`, watts: seg.power_watts });
    } else if (type === 'intervals') {
      const reps = seg.reps || seg.repetitions || 4;
      const onDur = seg.on_duration || seg.onDuration || seg.workDuration || 180;
      const offDur = seg.off_duration || seg.offDuration || seg.restDuration || 120;
      const onZone = normalizeZone(seg.on_zone || seg.onZone || seg.zone);
      const offZone = normalizeZone(seg.off_zone || seg.offZone) || 'zone1';
      for (let i = 0; i < reps; i++) {
        bars.push({ zone: onZone, duration: onDur, label: `ON ${onZone.replace('zone', 'Z')}`, watts: seg.on_power_watts });
        bars.push({ zone: offZone, duration: offDur, label: `OFF`, watts: seg.off_power_watts });
      }
    }
  }
  return bars;
}

function formatDuration(seconds: number): string {
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m}min`;
  return `${Math.floor(m / 60)}h${m % 60 > 0 ? (m % 60) + 'min' : ''}`;
}

export default function IntervalChart({ intervals, height = 60, showTooltips = true, showLabels = false }: IntervalChartProps) {
  if (!intervals || intervals.length === 0) return null;

  const bars = expandIntervals(intervals);
  const totalDuration = bars.reduce((sum, b) => sum + b.duration, 0);
  if (totalDuration === 0) return null;

  const totalMinutes = Math.round(totalDuration / 60);
  const tickInterval = totalMinutes <= 15 ? 2 : totalMinutes <= 30 ? 5 : totalMinutes <= 60 ? 10 : 15;
  const ticks: { label: string; position: number }[] = [];
  for (let m = 0; m <= totalMinutes; m += tickInterval) {
    const h = Math.floor(m / 60);
    const mm = m % 60;
    ticks.push({
      label: h > 0 ? `${h}:${mm.toString().padStart(2, '0')}` : `${mm}'`,
      position: totalMinutes > 0 ? (m / totalMinutes) * 100 : 0,
    });
  }

  return (
    <div className="interval-chart-container">
      <div className="interval-chart-bars" style={{ height }}>
        {bars.map((bar, i) => {
          const pct = (bar.duration / totalDuration) * 100;
          const showInlineLabel = showLabels && pct > 8;
          return (
            <div
              key={i}
              className="interval-chart-bar"
              style={{
                flexGrow: bar.duration,
                height: `${getHeight(bar.zone)}%`,
                backgroundColor: getColor(bar.zone),
              }}
              title={showTooltips ? `${bar.label}${bar.watts ? ` ${bar.watts}W` : ''} · ${formatDuration(bar.duration)}` : undefined}
            >
              {showInlineLabel && (
                <span className="interval-chart-bar-label">
                  {bar.label}{bar.watts ? ` ${bar.watts}W` : ''}
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div className="interval-chart-axis">
        {ticks.map((t, i) => (
          <span key={i} style={{ position: 'absolute', left: `${t.position}%`, transform: 'translateX(-50%)' }}>
            {t.label}
          </span>
        ))}
      </div>
    </div>
  );
}
